import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Toaster } from '@components/ui/toaster'

// Layouts and shared components (eager loaded)
import { AuthProvider } from '@store/auth/AuthProvider'
import { CartProvider } from '@store/cart/CartProvider'
import { CartModal } from '@components/shared/CartModal'
import { CartReminder } from '@components/shared/CartReminder'
import { MainLayout } from '@components/layout/MainLayout'
import { ProtectedRoute } from '@components/auth/ProtectedRoute'
import { AdminProtectedRoute } from '@components/admin/AdminProtectedRoute'
import { AdminLayout } from '@components/admin/AdminLayout'
import { LoadingSpinner } from '@components/ui/loading-spinner'

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@pages/auth/LoginPage'))
const AdminLoginPage = lazy(() => import('@pages/admin/AdminLoginPage'))
const AdminDocumentsPage = lazy(() => import('@pages/admin/AdminDocumentsPage'))
const AdminTicketsPage = lazy(() => import('@pages/admin/AdminTicketsPage'))
const AdminTicketDetallePage = lazy(() => import('@pages/admin/AdminTicketDetallePage'))
const AdminConfigPage = lazy(() => import('@pages/admin/AdminConfigPage'))
const AdminCambiosPage = lazy(() => import('@pages/admin/AdminCambiosPage'))
const CambiosPage = lazy(() => import('@pages/cambios/CambiosPage'))
const DashboardPage = lazy(() => import('@pages/dashboard/DashboardPage'))
const PendientesPage = lazy(() => import('@pages/pendientes/PendientesPage'))
const OrdersPage = lazy(() => import('@pages/orders/OrdersPage'))
const EstupefacientesPage = lazy(() => import('@pages/estupefacientes/EstupefacientesPage'))
const InformeResumenPage = lazy(() => import('@pages/estupefacientes/InformeResumenPage'))
const InformeDetalladoPage = lazy(() => import('@pages/estupefacientes/InformeDetalladoPage'))
const DisponibilidadPage = lazy(() => import('@pages/disponibilidad/DisponibilidadPage'))
const SMSPage = lazy(() => import('@pages/sms/SMSPage'))
const PartialDeliveriesPage = lazy(() => import('@pages/partial-deliveries/PartialDeliveriesPage'))
const AportacionesPage = lazy(() => import('@pages/aportaciones/AportacionesPage'))
const CondicionesPage = lazy(() => import('@pages/condiciones/CondicionesPage'))
const DevolucionesPage = lazy(() => import('@pages/devoluciones/DevolucionesPage'))
const DevolucionesDSPage = lazy(() => import('@pages/logistica-inversa/DevolucionesDSPage'))
const CedifaPage = lazy(() => import('@pages/logistica-inversa/CedifaPage'))
const InvoicesPage = lazy(() => import('@pages/invoices/InvoicesPage'))
const CalendarPage = lazy(() => import('@pages/invoices/CalendarPage'))
const PendingDuePage = lazy(() => import('@pages/invoices/PendingDuePage'))
const DeliveryNotesPage = lazy(() => import('@pages/delivery-notes/DeliveryNotesPage'))
const ContrastarAlbaranesPage = lazy(() => import('@pages/delivery-notes/ContrastarAlbaranesPage'))
const BuscarCubetaPage = lazy(() => import('@pages/delivery-notes/BuscarCubetaPage'))
const AlbaranesVencimientoPage = lazy(() => import('@pages/delivery-notes/AlbaranesVencimientoPage'))
const DocumentsPage = lazy(() => import('@pages/documents/DocumentsPage'))
const RecepcionPage = lazy(() => import('@pages/reception/RecepcionPage'))
const TransferFacilPage = lazy(() => import('@pages/transfers/TransferFacilPage'))
const TransferFacilAgrupadoPage = lazy(() => import('@pages/transfers/TransferFacilAgrupadoPage'))
const TransferGrupoPage = lazy(() => import('@pages/transfers/TransferGrupoPage'))
const TransferGrupoAgrupadoPage = lazy(() => import('@pages/transfers/TransferGrupoAgrupadoPage'))
const GestionTransferPage = lazy(() => import('@pages/transfers/GestionTransferPage'))
const ProfilePage = lazy(() => import('@pages/profile/ProfilePage'))
const UsersPage = lazy(() => import('@pages/users/UsersPage'))
const SecurityCodePage = lazy(() => import('@pages/attendance/SecurityCodePage'))
const DownloadsPage = lazy(() => import('@pages/attendance/DownloadsPage'))
const NotFoundPage = lazy(() => import('@pages/error/NotFoundPage'))
const TramoPage = lazy(() => import('@pages/informes-compras/TramoPage'))
const NivelAcofarmaPage = lazy(() => import('@pages/informes-compras/NivelAcofarmaPage'))
const HistoComprasPage = lazy(() => import('@pages/informes-compras/HistoComprasPage'))
const CategoriasPage = lazy(() => import('@pages/informes-compras/CategoriasPage'))
const VeterinariosPage = lazy(() => import('@pages/informes-compras/VeterinariosPage'))
const EncargosPage = lazy(() => import('@pages/encargos/EncargosPage'))
const AltasPage = lazy(() => import('@pages/altas/AltasPage'))
const DelegadosPage = lazy(() => import('@pages/cooperativa/DelegadosPage'))
const FarmaturnPage = lazy(() => import('@pages/farmaturn/FarmaturnPage'))
const ConsumiblesPage = lazy(() => import('@pages/consumibles/ConsumiblesPage'))
const IncidenciasPage = lazy(() => import('@pages/incidencias/IncidenciasPage'))
const MisTicketsPage = lazy(() => import('@pages/tickets/MisTicketsPage'))
const NuevoTicketPage = lazy(() => import('@pages/tickets/NuevoTicketPage'))
const TicketDetallePage = lazy(() => import('@pages/tickets/TicketDetallePage'))
const LogisticaInversaBIPage = lazy(() => import('@pages/analisis-datos/LogisticaInversaBIPage'))
const ComprasBIPage = lazy(() => import('@pages/analisis-datos/ComprasBIPage'))
const EstupefacientesBIPage = lazy(() => import('@pages/analisis-datos/EstupefacientesBIPage'))

// Preload function for critical pages
const preloadDashboard = () => {
  const DashboardModule = import('@pages/dashboard/DashboardPage')
  return DashboardModule
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Suspense fallback={<LoadingSpinner fullScreen />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* Admin (Supabase Auth, independent from socio login) */}
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route element={<AdminProtectedRoute />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin/documentos" element={<AdminDocumentsPage />} />
              <Route path="/admin/tickets" element={<AdminTicketsPage />} />
              <Route path="/admin/tickets/:id" element={<AdminTicketDetallePage />} />
              <Route path="/admin/config" element={<AdminConfigPage />} />
              <Route path="/admin/cambios" element={<AdminCambiosPage />} />
            </Route>
          </Route>
          
          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              <Route 
                path="/" 
                element={<DashboardPage />} 
                handle={{ preload: preloadDashboard }}
              />
              <Route path="/pendientes" element={<PendientesPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/estupefacientes" element={<EstupefacientesPage />} />
              <Route path="/estupefacientes/pedidos" element={<EstupefacientesPage />} />
              <Route path="/estupefacientes/informe-resumen" element={<InformeResumenPage />} />
              <Route path="/estupefacientes/informe-detallado" element={<InformeDetalladoPage />} />
              <Route path="/aviso-disponibilidad" element={<DisponibilidadPage />} />
              <Route path="/sms" element={<SMSPage />} />
              <Route path="/partial-deliveries" element={<PartialDeliveriesPage />} />
              <Route path="/aportaciones" element={<AportacionesPage />} />
              <Route path="/condiciones" element={<CondicionesPage />} />
              <Route path="/devoluciones" element={<DevolucionesPage />} />
              <Route path="/devoluciones-ds" element={<DevolucionesDSPage />} />
              <Route path="/cedifa" element={<CedifaPage />} />
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route path="/invoices/calendar" element={<CalendarPage />} />
              <Route path="/invoices/pending-due" element={<PendingDuePage />} />
              <Route path="/delivery-notes" element={<DeliveryNotesPage />} />
              <Route path="/delivery-notes/contrast" element={<ContrastarAlbaranesPage />} />
              <Route path="/albaranes-vencimiento" element={<AlbaranesVencimientoPage />} />
              <Route path="/buscar-cubeta" element={<BuscarCubetaPage />} />
              <Route path="/documentos" element={<DocumentsPage />} />
              <Route path="/recepcion" element={<RecepcionPage />} />
              <Route path="/attendance/security-code" element={<SecurityCodePage />} />
              <Route path="/attendance/downloads" element={<DownloadsPage />} />
              <Route path="/transfers/facil" element={<TransferFacilPage />} />
              <Route path="/transfers/gestion" element={<GestionTransferPage />} />
              <Route path="/transfers/facil-agrupado" element={<TransferFacilAgrupadoPage />} />
              <Route path="/transfers/grupo" element={<TransferGrupoPage />} />
              <Route path="/transfers/grupo-agrupado" element={<TransferGrupoAgrupadoPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/informes-compras/tramo" element={<TramoPage />} />
              <Route path="/informes-compras/nivel-acofarma" element={<NivelAcofarmaPage />} />
              <Route path="/informes-compras/histo-compras" element={<HistoComprasPage />} />
              <Route path="/informes-compras/categorias" element={<CategoriasPage />} />
              <Route path="/informes-compras/vet-antibioticos" element={<VeterinariosPage />} />
              <Route path="/analisis-datos/logistica-inversa" element={<LogisticaInversaBIPage />} />
              <Route path="/analisis-datos/compras" element={<ComprasBIPage />} />
              <Route path="/analisis-datos/estupefacientes" element={<EstupefacientesBIPage />} />
              <Route path="/encargos" element={<EncargosPage />} />
              <Route path="/altas" element={<AltasPage />} />
              <Route path="/delegados" element={<DelegadosPage />} />
              <Route path="/farmaturn" element={<FarmaturnPage />} />
              <Route path="/consumibles" element={<ConsumiblesPage />} />
              <Route path="/incidencias" element={<IncidenciasPage />} />
              <Route path="/soporte" element={<MisTicketsPage />} />
              <Route path="/cambios" element={<CambiosPage />} />
              <Route path="/soporte/nuevo" element={<NuevoTicketPage />} />
              <Route path="/soporte/:id" element={<TicketDetallePage />} />
            </Route>
          </Route>
          
          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <CartReminder />
      <Toaster />
      <CartModal />
      </CartProvider>
    </AuthProvider>
  )
}

export default App
